import { default as classNames } from "classnames";
import { ArrowLeft, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ModeToggle } from "../theme-mode-toggle";

import { Button } from "./button";
import { UserInfo } from "./user-nav";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { config } from "@/configs/env.config";
import { useAuth } from "@/providers/AuthContextProvider";
import { Route } from "@/routes/routes";
import { isPermitted } from "@/utils/permission-manager";

const STAGE_BADGES: Record<
  string,
  { label: string; short: string; classes: string }
> = {
  dev: {
    label: "DEV",
    short: "D",
    classes: "border-sky-500/50 bg-sky-500/10 text-sky-500",
  },
  staging: {
    label: "STAGING",
    short: "S",
    classes: "border-amber-500/50 bg-amber-500/10 text-amber-500",
  },
  prod: {
    label: "PRODUCTION",
    short: "P",
    classes: "border-red-500/60 bg-red-500/15 text-red-500",
  },
};

const StageBadge = ({ collapsed }: { collapsed: boolean }) => {
  const stage = (config.STAGE as string) || "unknown";
  const badge = STAGE_BADGES[stage] ?? {
    label: stage.toUpperCase(),
    short: stage.charAt(0).toUpperCase(),
    classes: "border-border bg-muted text-muted-foreground",
  };

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={classNames(
                "mx-3 my-2 flex h-6 items-center justify-center rounded-md border text-xs font-bold tracking-wide",
                badge.classes
              )}
            >
              {badge.short}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">{badge.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      className={classNames(
        "mx-3 my-2 flex h-7 items-center justify-center rounded-md border text-xs font-bold tracking-wide",
        badge.classes
      )}
    >
      {badge.label}
    </div>
  );
};

type Props = {
  collapsed: boolean;
  onToggle(collapsed: boolean): void;
};

const Sidebar = ({ collapsed, onToggle }: Props) => {
  const { userData } = useAuth();
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const location = useLocation();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<Route[]>([]);

  useEffect(() => {
    const importMenuItems = async () => {
      // dynamic import due to an issue
      const routesModule = await import("@/routes/routes");
      const fetchedMenuItems = routesModule.getMenuItems();

      const filteredMenuItems = fetchedMenuItems.filter((item) =>
        item.permission ? isPermitted(item.permission, userData) : true
      );

      setMenuItems(filteredMenuItems);
    };

    importMenuItems();
  }, []);

  return (
    <div
      className={classNames({
        "fixed md:static md:translate-x-0 z-20 bg-background": true,
        "transition-all duration-300 ease-in-out": true,
        "w-[300px]": !collapsed,
        "w-16": collapsed,
        "border-r border-border": true,
      })}
    >
      <div
        className={classNames({
          "flex flex-col justify-between h-screen md:h-full sticky inset-0 ":
            true,
        })}
      >
        <div
          className={classNames({
            "flex items-center border-b border-border transition-none": true,
            "p-4 justify-between": !collapsed,
            "py-4 flex-col gap-1 items-center": collapsed,
          })}
        >
          <Button
            className="grid w-10 h-10 opacity-0 place-content-center md:opacity-100"
            onClick={() => navigate(-1)}
            size={"icon"}
            variant={"ghost"}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            className="grid w-10 h-10 opacity-0 place-content-center md:opacity-100"
            onClick={() => onToggle(!collapsed)}
            size={"icon"}
            variant={"ghost"}
          >
            <Icon className="w-5 h-5" />
          </Button>
        </div>
        <StageBadge collapsed={collapsed} />
        <nav className="flex-grow">
          <ul
            className={classNames({
              "my-2 flex flex-col gap-2 items-stretch": true,
            })}
          >
            {menuItems.map((item, index) => {
              return (
                <li
                  className={classNames({
                    "flex transition-all duration-150 text-md border-b border-border rounded-md p-2 mx-3":
                      true,
                    "hover:bg-hover hover:font-semibold": true,
                    "font-normal": !location.pathname.startsWith(item.path),
                    "font-semibold bg-accent": location.pathname.startsWith(
                      item.path
                    ),
                    "gap-4": !collapsed,
                    "w-10 h-10": collapsed,
                  })}
                  key={index}
                >
                  {item.children ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger>
                          <Link className="flex gap-2 items-center justify-start" to={item.path}>
                            {item.icon}
                            <span className="text-primary">
                              {!collapsed && item.label}
                            </span>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <ul
                            className={classNames({
                              "flex flex-col gap-2 items-stretch": true,
                            })}
                          >
                            {item.children.filter((c) => c.showInMenu).map((child, index) => {
                              return (
                                <li
                                  className={classNames({
                                    "flex transition-all duration-150 text-md border-b border-border rounded-md p-2 mx-3":
                                      true,
                                    "hover:bg-hover hover:font-semibold": true,
                                    "font-normal":
                                      !location.pathname.startsWith(child.path),
                                    "font-semibold bg-accent":
                                      location.pathname.startsWith(child.path),
                                  })}
                                  key={index}
                                >
                                  <Link
                                    className="w-full flex gap-2 items-center justify-start"
                                    to={child.path}
                                  >
                                    {child.icon}
                                    <span className="text-primary">
                                      {child.label}
                                    </span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Link
                      className={classNames({
                        "w-full flex gap-2 items-center justify-start":
                          !collapsed,
                      })}
                      to={item.path}
                    >
                      <TooltipProvider delayDuration={200}>
                        <Tooltip {...(!collapsed ? { open: false } : {})}>
                          <TooltipTrigger>{item.icon}</TooltipTrigger>
                          <TooltipContent side="right">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <span className="text-primary">
                        {!collapsed && item.label}
                      </span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        <div
          className={classNames({
            flex: true,
            "justify-center": collapsed,
            "justify-between": !collapsed,
            "transition-all duration-300": true,
            "p-2 mx-3 gap-4 ": true,
          })}
        >
          <TooltipProvider delayDuration={200}>
            <Tooltip {...(!collapsed ? { open: false } : {})}>
              <TooltipTrigger>
                <UserInfo />
              </TooltipTrigger>
              <TooltipContent side="right">
                <span className="font-medium">
                  {userData?.attributes?.email}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!collapsed && <ModeToggle />}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
